import { Avatar, Box, theme } from '@salecycle/ui-component-library';
import { FC, useState } from 'react';
import styled from 'styled-components';
import Bubble from './Bubble';
import { State as CampaignState } from 'entities/lifecycle/lifeCycle';
import { getStatusColor } from '../../utils/helpers';

const StyledBlock = styled.div`
  background-color: ${({ theme }) => theme.vars.newColor.white};
  position: relative;
  margin-bottom: 1.6rem;

  .list {
    .title {
      font-weight: 600;
    }
  }
`;

const Wrapper = styled.div`
  border: 1px solid ${({ theme }) => theme.vars.newColor.lightGS1};
`;

const Meta = styled.button`
  display: flex;
  width: 100%;
  text-align: left;
  background-color: transparent;
  border: 0;
  margin: 0;
  padding: 0.7rem 2rem;
`;

const Content = styled.div`
  align-items: center;
  display: flex;
  width: 100%;
`;

const BlockIcon = styled.div`
  margin-right: 1.7rem;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${({ theme }) => theme.vars.newColor.white};
`;

const List = styled.ul`
  list-style-type: none;
  margin-bottom: 0;
`;

const Item = styled.li``;

const Title = styled.span`
  font-weight: 600;
`;

const Preview = styled.div`
  position: relative;
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem 2rem 0.7rem;

  > * {
    &:not(:last-child) {
      margin-right: 1.2rem;
    }
  }
`;

interface IBlockCommonProps {
  valid?: boolean;
  iconColor?: string;
  campaignState: CampaignState;
}

export interface IBlockProps extends IBlockCommonProps {
  title: string;
  value: JSX.Element | string;
  secondValue?: string;
  icon: string;
  controls?: JSX.Element;
  menu?: JSX.Element;
  form: JSX.Element;
  preview?: JSX.Element;
  details?: JSX.Element;
  disableOpen: boolean;
}

const defaultProps = {
  valid: true,
  iconColor: theme.vars.newColor.darkGS1,
};

const Block: FC<IBlockProps> = props => {
  const [isOpen, setIsOpen] = useState(false);
  let timeOutId = null;

  const statusColor = getStatusColor(props.campaignState, theme);
  const iconColor = props.valid
    ? statusColor
      ? statusColor
      : props.iconColor
      ? props.iconColor
      : theme.vars.newColor.darkGS1
    : theme.vars.newColor.darkGS3;

  const onClickHandler = () => {
    if (!props.disableOpen) {
      setIsOpen(!isOpen);
    }
  };

  // We close the popover on the next tick by using setTimeout.
  // This is necessary because we need to first check if
  // another child of the element has received focus as
  // the blur event fires prior to the new focus event.
  const onBlurHandler = () => {
    timeOutId = setTimeout(() => {
      setIsOpen(false);
    });
  };

  // If a child receives focus, do not close the popover.
  const onFocusHandler = () => {
    clearTimeout(timeOutId);
  };
  return (
    <StyledBlock onFocus={onFocusHandler} onBlur={onBlurHandler} data-testid="Block">
      <Wrapper>
        <Content>
          <Meta onClick={onClickHandler} type="button" data-testid="BlockMeta">
            <BlockIcon>
              <Avatar icon={props.icon} size="large" bg={iconColor} />
            </BlockIcon>

            <List>
              <Item data-testid="CampaignEditorBlockTitle">
                <Title>{props.title}</Title>
              </Item>
              <Item data-testid="CampaignEditorBlockValue">{props.value}</Item>
              {props.secondValue && <Item data-testid="CampaignEditorBlockSecondValue">{props.secondValue}</Item>}
            </List>
          </Meta>

          {props.menu}
        </Content>

        {props.preview && (
          <Preview>
            <Box horizontalPositioning="center" data-testid="Preview">
              {props.preview}
            </Box>
          </Preview>
        )}

        {props.controls && <Controls data-testid="BlockControls">{props.controls}</Controls>}

        {props.details && (
          <Content>
            <Meta onClick={onClickHandler} type="button" data-testid="CampaignEditorDetailsMeta">
              {props.details}
            </Meta>
          </Content>
        )}
      </Wrapper>

      {isOpen && <Bubble>{props.form}</Bubble>}
    </StyledBlock>
  );
};

Block.defaultProps = defaultProps;

export default Block;
